/**
 * Normalize Jira REST / ADF comment & description payloads to plain text or HTML.
 * Handles Atlassian Document Format including mention nodes (otherwise invisible in UI).
 */

export type JiraAdfAttrs = Record<string, unknown>;

export type JiraAdfMark = {
  type: string;
  attrs?: Record<string, unknown>;
};

export type JiraAdfNode = {
  type?: string;
  text?: string;
  content?: unknown[];
  attrs?: JiraAdfAttrs;
  marks?: JiraAdfMark[];
};

function mentionToPlain(attrs: JiraAdfAttrs | undefined): string {
  if (!attrs) return "";
  const raw =
    (typeof attrs.text === "string" && attrs.text) ||
    (typeof attrs.displayName === "string" && attrs.displayName) ||
    (typeof attrs.label === "string" && attrs.label) ||
    "";
  const s = String(raw).trim();
  if (!s) return "";
  return s.startsWith("@") ? s : `@${s}`;
}

export function jiraRichTextToPlainString(value: unknown): string {
  if (value == null) return "";
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed.startsWith("{")) {
      try {
        return jiraRichTextToPlainString(JSON.parse(value) as JiraAdfNode);
      } catch {
        return value;
      }
    }
    return value;
  }
  if (typeof value !== "object") return String(value);

  const node = value as JiraAdfNode;
  const type = node.type;

  if (type === "mention") {
    return mentionToPlain(node.attrs);
  }

  if (type === "hardBreak") return "\n";

  if (type === "emoji" && node.attrs) {
    const t = node.attrs.text;
    if (typeof t === "string" && t) return t;
    const shortName = node.attrs.shortName;
    if (typeof shortName === "string" && shortName) return `:${shortName}:`;
  }

  if (typeof node.text === "string") return node.text;

  if (Array.isArray(node.content)) {
    return node.content.map((child) => jiraRichTextToPlainString(child)).join("");
  }

  return "";
}

/* ── ADF → HTML ────────────────────────────────────────────────────── */

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

export function jiraAdfToHtml(value: unknown): string {
  if (value == null) return "";
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed.startsWith("{")) {
      try {
        return jiraAdfToHtml(JSON.parse(value) as JiraAdfNode);
      } catch {
        return escapeHtml(value);
      }
    }
    return escapeHtml(value).replace(/\n/g, "<br />");
  }
  if (typeof value !== "object")
    return escapeHtml(String(value)).replace(/\n/g, "<br />");

  const node = value as JiraAdfNode;
  const type = node.type;

  // Inline: text with optional marks (bold, italic, link, etc.)
  if (type === "text") {
    let html = escapeHtml(node.text || "").replace(/\n/g, "<br />");
    if (Array.isArray(node.marks)) {
      for (const mark of node.marks) {
        if (mark.type === "strong") html = `<strong>${html}</strong>`;
        else if (mark.type === "em") html = `<em>${html}</em>`;
        else if (mark.type === "underline") html = `<u>${html}</u>`;
        else if (mark.type === "strike") html = `<s>${html}</s>`;
        else if (mark.type === "code") html = `<code>${html}</code>`;
        else if (mark.type === "link" && mark.attrs?.href) {
          const href = escapeHtml(String(mark.attrs.href));
          html = `<a href="${href}" target="_blank" rel="noopener noreferrer">${html}</a>`;
        }
      }
    }
    return html;
  }
  if (type === "mention") return escapeHtml(mentionToPlain(node.attrs));
  if (type === "hardBreak") return "<br />";
  if (type === "emoji" && node.attrs) {
    const t = node.attrs.text;
    if (typeof t === "string" && t) return escapeHtml(t);
    const shortName = node.attrs.shortName;
    if (typeof shortName === "string" && shortName) return escapeHtml(`:${shortName}:`);
  }

  // Block nodes
  const children = Array.isArray(node.content)
    ? node.content.map((child) => jiraAdfToHtml(child)).join("")
    : "";

  switch (type) {
    case "doc":
      return children;
    case "paragraph":
      return `<p>${children}</p>`;
    case "bulletList":
      return `<ul>${children}</ul>`;
    case "orderedList": {
      const start = (node.attrs?.order as number) ?? 1;
      return start === 1
        ? `<ol>${children}</ol>`
        : `<ol start="${start}">${children}</ol>`;
    }
    case "listItem":
      return `<li>${children}</li>`;
    case "heading": {
      const level = Math.min(Math.max((node.attrs?.level as number) || 3, 1), 6);
      return `<h${level}>${children}</h${level}>`;
    }
    case "codeBlock":
      return `<pre><code>${children}</code></pre>`;
    case "blockquote":
      return `<blockquote>${children}</blockquote>`;
    case "rule":
      return "<hr />";
    default:
      return children;
  }
}
